import React, { useState, useEffect, useMemo } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const STATUS_OPTIONS = [
  { value: "IP", label: "In Process" },
  { value: "IT", label: "In Process & Transfer" },
  { value: "CT", label: "Close & Transfer" },
  { value: "CL", label: "Closed" },
];

export default function ResolverAction() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const empId    = parseInt(localUser?.id,       10);

  const [pendingTickets,  setPendingTickets]  = useState([]);
  const [progressTypes,   setProgressTypes]   = useState([]);
  const [resolverMasters, setResolverMasters] = useState([]);

  const [form, setForm] = useState({
    ticketId:      "",
    progressId:    "",
    resolverId:    "",
    resolverEmpId: "",
    status:        "IP",
    remarks:       "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error,   setError]   = useState("");

  const loadTickets = () =>
    fetch(`${API_ROOT}/api/complaints/resolver-pending-tickets?moduleId=${moduleId}&empId=${empId}`)
      .then(r => r.json())
      .then(d => {
        const list = d.data || [];
        setPendingTickets(list);
        if (list.length) setForm(prev => ({ ...prev, ticketId: String(list[0].TicketID) }));
      })
      .catch(() => {});

  useEffect(() => {
    if (moduleId !== 9) return;
    const base = `${API_ROOT}/api/complaints`;

    loadTickets();

    fetch(`${base}/progress-types?moduleId=${moduleId}`)
      .then(r => r.json())
      .then(d => {
        const list = d.data || [];
        setProgressTypes(list);
        if (list.length) setForm(prev => ({ ...prev, progressId: String(list[0].ProgressID) }));
      })
      .catch(() => {});

    fetch(`${base}/resolver-masters?moduleId=${moduleId}`)
      .then(r => r.json())
      .then(d => {
        const list = d.data || [];
        setResolverMasters(list);
        if (list.length) setForm(prev => ({ ...prev, resolverId: String(list[0].ResolverID) }));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch employee ID whenever resolverId changes
  useEffect(() => {
    if (!form.resolverId || moduleId !== 9) return;
    fetch(`${API_ROOT}/api/complaints/resolver-empid?moduleId=${moduleId}&resolverId=${form.resolverId}`)
      .then(r => r.json())
      .then(d => setForm(prev => ({ ...prev, resolverEmpId: d.employeeId ? String(d.employeeId) : "" })))
      .catch(() => {});
  }, [form.resolverId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setMessage(""); setLoading(true);
    try {
      const res = await fetch(`${API_ROOT}/api/complaints/resolver-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          moduleId,
          empId,
          ticketId:      form.ticketId,
          progressId:    form.progressId,
          resolverId:    form.resolverId,
          resolverEmpId: form.resolverEmpId,
          status:        form.status,
          remarks:       form.remarks,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save.");
      setMessage(data.message || "Record Updated successfully");
      setForm(prev => ({ ...prev, remarks: "" }));
      loadTickets();
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  if (moduleId !== 9) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">Pending Complaints</h3>

      {error   && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <form onSubmit={onSubmit}>
        <div className="col-lg-10">

          <div className="mb-2">
            <label className="form-label">Complaint Category</label>
            <select
              className="form-select"
              name="ticketId"
              value={form.ticketId}
              onChange={onChange}
              required
            >
              {pendingTickets.length === 0 && (
                <option value="">No pending complaints</option>
              )}
              {pendingTickets.map(t => (
                <option key={t.TicketID} value={String(t.TicketID)}>
                  {t.ComplaintDescription} - {t.Remark} - {t.ResiType} - {t.ResidenceID} - {t.EmpName} - - {t.Mobile} - ({t.TicketID})
                </option>
              ))}
            </select>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-sm-6">
              <label className="form-label">Progress</label>
              <select
                className="form-select"
                name="progressId"
                value={form.progressId}
                onChange={onChange}
                required
              >
                {progressTypes.map(p => (
                  <option key={p.ProgressID} value={String(p.ProgressID)}>{p.ProgressDetails}</option>
                ))}
              </select>
            </div>

            <div className="col-sm-4">
              <label className="form-label">Transferred To</label>
              <select
                className="form-select"
                name="resolverId"
                value={form.resolverId}
                onChange={onChange}
                required
              >
                {resolverMasters.map(r => (
                  <option key={r.ResolverID} value={String(r.ResolverID)}>{r.Resolver}</option>
                ))}
              </select>
            </div>

            <div className="col-sm-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={form.status}
                onChange={onChange}
                required
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Remarks</label>
            <input
              className="form-control"
              type="text"
              name="remarks"
              value={form.remarks}
              onChange={onChange}
              placeholder="Description...*"
              required
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading || pendingTickets.length === 0}>
            {loading ? "Saving..." : "Save Now"}
          </button>

        </div>
      </form>
    </div>
  );
}
